/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { Box, Divider, Stack, styled, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectCurrentFarmerAccount } from "../../../azure-b2c/storage";
import { CartItem } from "../../../cart/storage/cart.types";
import { selectStoreById } from "../../../stores/storage/stores.slice";
import { Quote } from "../../storage/types";
import { showQuoteAsTwoColumnLayout } from "../../utils/getQuoteStatus";

import { QuoteRejectionReason } from "./QuoteRejectionReason";
import { QuoteSummaryTotal } from "./QuoteSummaryTotal";

const DetailContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  background: colors.shade0,
  padding: "24px",
  gap: "24px",
  border: `1px solid ${colors.primary100}`,
  [theme.breakpoints.down("sm")]: {
    padding: "16px",
    gap: "16px",
  },
}));

export const QuoteDetailInfo = ({ quote, products }: { quote: Quote; products: CartItem[] }) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("lg"));
  const pickupStore = useSelector(selectStoreById(quote.delivery.pickupStoreId ?? undefined));

  const account = useSelector(selectCurrentFarmerAccount);

  const renderFreight = React.useMemo(() => {
    const delivery = quote.delivery;
    let details = null;

    if (delivery.deliveryAddress) {
      const address = delivery.deliveryAddress;
      const cityEtc = `${address.city}, ${address.postcode}`;
      details = (
        <Stack>
          <Typography variant="body2" fontWeight={700} color={theme.colors.primary500}>
            Deliver to
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {address.name}
          </Typography>
          <Typography variant="body2">{address.streetAddress}</Typography>
          <Typography variant="body2">{cityEtc}</Typography>
        </Stack>
      );
    }

    if (pickupStore) {
      details = (
        <Stack>
          <Typography variant="body2" fontWeight={700} color={theme.colors.primary500}>
            Pick up
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {pickupStore.orgUnitName}
          </Typography>
          <Typography variant="body2">{pickupStore.address}</Typography>
        </Stack>
      );
    }

    if (details) {
      return (
        <Stack spacing={2}>
          <Typography variant="h4" fontWeight={700} color={theme.colors.primary500}>
            Freight
          </Typography>
          {details}
        </Stack>
      );
    }

    return null;
  }, [quote.delivery, pickupStore]);

  const renderNotes = React.useMemo(() => {
    if (quote.notes.length > 0) {
      return (
        <Stack spacing={1}>
          <Typography variant="h4" fontWeight={700} color={theme.colors.primary500}>
            Notes
          </Typography>
          <Stack spacing={1}>
            {quote.notes.map((note) => {
              return (
                <Stack direction="row" spacing={1} key={note.sentAt.toString()}>
                  <Typography variant="body2" fontWeight={400} color={theme.colors.grey700}>
                    {`${note.name}: `}
                  </Typography>
                  <Typography variant="body2" fontWeight={400} color={theme.colors.grey900}>
                    {note.message}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      );
    }

    return null;
  }, [quote.notes]);

  const renderCustomerDetails = React.useMemo(() => {
    const contact = quote.contact;
    if (contact) {
      return (
        <Stack spacing={1}>
          <Typography variant="h4" fontWeight={700} color={theme.colors.primary500}>
            Customer details
          </Typography>
          <Stack>
            {contact.name ? (
              <Typography variant="body2" fontWeight={400}>
                {contact.name}
              </Typography>
            ) : null}
            {contact.email ? (
              <Typography variant="body2" fontWeight={400}>
                {contact.email}
              </Typography>
            ) : null}
            {contact.phone ? (
              <Typography variant="body2" fontWeight={400}>
                {contact.phone}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      );
    }

    return null;
  }, [quote.contact]);

  const twoColumnLayout = useMemo(() => {
    if (quote) {
      return showQuoteAsTwoColumnLayout(quote);
    }
    return false;
  }, [quote, account]);

  const emptyBox =
    !quote.delivery.deliveryAddress && !pickupStore && quote.notes.length <= 0 && !quote.contact;

  return emptyBox ? null : (
    <DetailContainer>
      <Stack spacing={2}>
        {renderFreight}
        {renderNotes}
        <Divider />
        {renderCustomerDetails}
        <QuoteRejectionReason quote={quote} />
        {twoColumnLayout ? null : (
          <Stack spacing={4} pt={2}>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <QuoteSummaryTotal
                backgroundColor="transparent"
                accountName={account?.accountName}
                accountNumber={account?.accountNumber}
                products={products}
                freightChargeExcGst={quote?.delivery?.freightChargeExcGst}
                showDivider={false}
                title="Summary"
              />
              {isMd ? null : <Box flex={1} />}
            </Stack>
          </Stack>
        )}
      </Stack>
    </DetailContainer>
  );
};
